import React from 'react';
import { MdCheck, MdCheckBox, MdCheckBoxOutlineBlank, MdExpandMore, MdSearch } from 'react-icons/md';

export default function SearchableDropdown({
	label,
	placeholder = 'Select',
	options = [],
	value = '',
	onChange,
	onInputChange,
	className = '',
	disabled = false,
	loading = false,
	required = false,
	emptyMessage = 'No results found',
	allowCustom = false,
	showSearch,
	searchable,
	isSearchable,
	error,
	pageSize,
	// When true, `value` is an array and onChange receives the next array; the "" option clears it.
	multiple = false,
}) {
	const wrapperRef = React.useRef(null);
	const menuRef = React.useRef(null);
	const searchInputRef = React.useRef(null);

	const [isOpen, setIsOpen] = React.useState(false);
	const [searchText, setSearchText] = React.useState('');
	const [placement, setPlacement] = React.useState('bottom');
	const [menuMaxHeight, setMenuMaxHeight] = React.useState(280);

	React.useLayoutEffect(() => {
		if (!isOpen || !wrapperRef.current) return;
		const updatePlacement = () => {
			if (!wrapperRef.current) return;
			const rect = wrapperRef.current.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const spaceBelow = viewportHeight - rect.bottom - 16;
			const spaceAbove = rect.top - 16;
			const defaultMax = 280;

			if (spaceBelow < 200 && spaceAbove > spaceBelow) {
				setPlacement('top');
				setMenuMaxHeight(Math.min(defaultMax, Math.max(140, spaceAbove)));
			} else {
				setPlacement('bottom');
				setMenuMaxHeight(Math.min(defaultMax, Math.max(140, spaceBelow)));
			}
		};

		updatePlacement();
		window.addEventListener('resize', updatePlacement);
		window.addEventListener('scroll', updatePlacement, true);
		return () => {
			window.removeEventListener('resize', updatePlacement);
			window.removeEventListener('scroll', updatePlacement, true);
		};
	}, [isOpen]);

	const shouldShowSearch =
		showSearch !== undefined
			? Boolean(showSearch)
			: searchable !== undefined
				? Boolean(searchable)
				: isSearchable !== undefined
					? Boolean(isSearchable)
					: options.length > 5;

	const selectedOption = React.useMemo(() => {
		if (multiple || value === null || value === undefined || value === '' || Number.isNaN(value)) return null;
		return options.find((option) => String(option.value) === String(value)) || null;
	}, [multiple, options, value]);

	const selectedValues = React.useMemo(() => {
		return multiple && Array.isArray(value) ? value.map(String) : [];
	}, [multiple, value]);

	const selectedLabels = React.useMemo(() => {
		if (!selectedValues.length) {
			return [];
		}
		const labelOf = new Map(options.map((option) => [String(option.value), option.label]));
		return selectedValues.map((v) => labelOf.get(v) ?? v);
	}, [options, selectedValues]);

	const filteredOptions = React.useMemo(() => {
		const term = searchText.trim().toLowerCase();

		if (!term) {
			return options;
		}

		// An option can carry extra searchText (e.g. other names it is known by) beyond its label.
		return options.filter((option) => String(option.searchText ?? option.label ?? '').toLowerCase().includes(term));
	}, [options, searchText]);

	// With pageSize set, options render in pages; the next page is appended as the menu nears its end.
	const [visibleCount, setVisibleCount] = React.useState(pageSize);

	React.useEffect(() => {
		setVisibleCount(pageSize);
		if (menuRef.current) {
			menuRef.current.scrollTop = 0;
		}
	}, [pageSize, searchText, isOpen]);

	const visibleOptions = React.useMemo(() => {
		return Number.isFinite(pageSize) ? filteredOptions.slice(0, visibleCount) : filteredOptions;
	}, [filteredOptions, pageSize, visibleCount]);
	const hasMore = visibleOptions.length < filteredOptions.length;

	const handleMenuScroll = (event) => {
		if (!hasMore) {
			return;
		}
		const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
		if (scrollTop + clientHeight >= scrollHeight - 48) {
			setVisibleCount((count) => count + pageSize);
		}
	};

	React.useEffect(() => {
		if (!isOpen) {
			setSearchText('');
			return;
		}

		const timer = window.setTimeout(() => {
			if (shouldShowSearch) {
				searchInputRef.current?.focus();
			}
		}, 0);

		return () => window.clearTimeout(timer);
	}, [isOpen, shouldShowSearch]);

	React.useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleClickOutside = (event) => {
			if (!wrapperRef.current?.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const displayValue = multiple
		? selectedLabels[0] || ''
		: selectedOption?.label || (allowCustom && value ? String(value) : '');
	const hasSelection = multiple ? selectedLabels.length > 0 : Boolean(selectedOption || (allowCustom && value));

	const handleSelect = (optionValue) => {
		if (multiple) {
			const key = String(optionValue);
			const current = Array.isArray(value) ? value : [];
			let next;
			if (key === '') {
				next = [];
			} else if (selectedValues.includes(key)) {
				next = current.filter((v) => String(v) !== key);
			} else {
				next = [...current, optionValue];
			}
			onChange?.(next);
			// Keep the menu open and the search term so several options can be picked in a row.
			return;
		}

		const isAlreadySelected =
			value !== null && value !== undefined && value !== '' && String(optionValue) === String(value);
		const nextValue = isAlreadySelected ? null : optionValue;
		onChange?.(nextValue);
		setSearchText('');
		setIsOpen(false);
	};

	const handleSearchInputChange = (event) => {
		const inputValue = event.target.value;
		setSearchText(inputValue);
		if (allowCustom) {
			onInputChange?.(inputValue);
		}
	};

	return (
		<div className="position-relative" ref={wrapperRef}>
			{label ? (
				<label className="form-label fw-semibold mb-2">
					{label}
					{required ? <span className="text-danger ms-1">*</span> : null}
				</label>
			) : null}

			<button
				type="button"
				className={`form-control d-flex align-items-center justify-content-between text-start ${error ? 'is-invalid' : ''} ${className}`}
				onClick={() => !disabled && setIsOpen((previous) => !previous)}
				disabled={disabled}
				style={{ height: 40, lineHeight: 1.3 }}
			>
				<span
					className={`d-block w-100 text-truncate ${hasSelection ? 'text-dark' : 'text-secondary'}`}
				>
					{displayValue || placeholder}
				</span>
				{selectedLabels.length > 1 ? (
					<span
						className="badge rounded-pill bg-light text-primary border ms-2 flex-shrink-0"
						title={selectedLabels.join('\n')}
					>
						+{selectedLabels.length - 1}
					</span>
				) : null}
				<MdExpandMore size={18} className="text-secondary flex-shrink-0 ms-2" />
			</button>

			<div
				ref={menuRef}
				className={`dropdown-menu w-100 p-2 shadow overflow-y-auto overflow-x-hidden ${isOpen ? 'show' : ''}`}
				style={{
					position: 'absolute',
					zIndex: 1060,
					maxHeight: menuMaxHeight,
					...(placement === 'top'
						? { bottom: '100%', top: 'auto', marginBottom: '6px', marginTop: 0 }
						: { top: '100%', bottom: 'auto', marginTop: '6px', marginBottom: 0 }
					),
				}}
				onScroll={handleMenuScroll}
			>
				{shouldShowSearch && (
					<div className="position-relative mb-2">
						<span
							className="position-absolute top-50 translate-middle-y text-secondary"
							style={{ left: 10, lineHeight: 0 }}
						>
							<MdSearch size={16} />
						</span>
						<input
							ref={searchInputRef}
							type="text"
							className="form-control form-control-sm"
							placeholder="Search..."
							value={searchText}
							onChange={handleSearchInputChange}
							onClick={(event) => event.stopPropagation()}
							disabled={loading}
							style={{ paddingLeft: 30 }}
						/>
					</div>
				)}

				{loading ? (
					<div className="px-1 py-1">
						<div className="placeholder-glow mb-2">
							<span className="placeholder col-12 rounded-2" style={{ height: 28 }} />
						</div>
						<div className="placeholder-glow mb-2">
							<span className="placeholder col-10 rounded-2" style={{ height: 28 }} />
						</div>
						<div className="placeholder-glow">
							<span className="placeholder col-11 rounded-2" style={{ height: 28 }} />
						</div>
					</div>
				) : filteredOptions.length > 0 ? (
					<>
						{visibleOptions.map((option) => {
							const optionKey = String(option.value);
							const isSelected = multiple
								? optionKey === ''
									? selectedValues.length === 0
									: selectedValues.includes(optionKey)
								: value !== null &&
								  value !== undefined &&
								  value !== '' &&
								  !Number.isNaN(value) &&
								  optionKey === String(value);

							return (
								<button
									key={option.value}
									type="button"
									className={`dropdown-item rounded-2 d-flex align-items-center text-wrap ${
										multiple ? 'gap-2' : 'justify-content-between'
									} ${isSelected ? 'hc-dropdown-item-selected' : ''}`}
									onClick={() => handleSelect(option.value)}
									aria-pressed={multiple ? isSelected : undefined}
								>
									{multiple ? (
										isSelected ? (
											<MdCheckBox size={18} className="flex-shrink-0" />
										) : (
											<MdCheckBoxOutlineBlank size={18} className="flex-shrink-0 text-secondary" />
										)
									) : null}
									<span>{option.label}</span>
									{!multiple && isSelected ? <MdCheck size={16} /> : null}
								</button>
							);
						})}
						{hasMore ? (
							<div className="px-2 pt-2 text-secondary small">
								Showing {visibleOptions.length} of {filteredOptions.length} — scroll for more
							</div>
						) : null}
					</>
				) : allowCustom && searchText.trim() ? (
					<button
						type="button"
						className="dropdown-item rounded-2 text-start"
						onClick={() => handleSelect(searchText.trim())}
					>
						Use "{searchText.trim()}"
					</button>
				) : (
					<div className="px-2 py-2 text-secondary small">{emptyMessage}</div>
				)}
			</div>
			{error ? <div className="invalid-feedback d-block">{error}</div> : null}
		</div>
	);
}
